import { useCallback, useMemo, useState } from 'react';
import { Chip, InputAdornment, OutlinedInput, Switch, Tab, Tabs, ThemeProvider } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import * as request from '../../../Common/Util/Request';
import * as Button from '../../../Common/Component/Button';
import Table from '../../../Common/Component/Table';
import { useLocationState } from '../../../Common/Util/CustomHooks';
import { EJE_THEME } from '../../../Common/Constant';
import './List.scss';

// 진행 상태 탭과 상태 칩이 함께 쓴다. value 는 서버 status 쿼리 값이고, 전체('')의 건수는 statusCounts.total 이다
const STATUSES = [
  { value: 'ongoing', label: '진행 중', color: 'success' },
  { value: 'upcoming', label: '예정', color: 'info' },
  { value: 'ended', label: '종료', color: 'default' },
  { value: '', label: '전체' },
];
const DEFAULT_STATUS = 'ongoing';

// dates 는 월요일부터 일요일까지 7자리 0/1 이다
const WEEKDAYS = '월화수목금토일';
const toWeekdays = (dates) => [...(dates || '')].map((day, index) => (day === '1' ? WEEKDAYS[index] : '')).join('');
const toHourMinute = (time) => (time ? time.slice(0, 5) : '');

// 한글 조합 중에 누른 Enter 는 검색으로 치지 않는다(조합이 끝날 때 keydown 이 한 번 더 온다)
const isEnter = (e) => e.key === 'Enter' && !e.nativeEvent.isComposing;

const OfflineClassesByScheduleList = () => {
  const [locationState, setLocationState] = useLocationState({ status: DEFAULT_STATUS, searchText: '' });
  // 이 화면을 개편 전에 연 탭의 기록 state 에는 status 가 없다
  const status = locationState.status ?? DEFAULT_STATUS;
  const [searchText, setSearchText] = useState(locationState.searchText || '');
  // 다시 마운트될 때마다 첫 조회 전에 빈 목록 안내가 잠깐 보이지 않게 조회 중으로 시작한다
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [list, setList] = useState([]);
  const [total, setTotal] = useState(0);
  const [statusCounts, setStatusCounts] = useState({});

  const fetchData = useCallback(
    async ({ pageIndex }) => {
      setLoading(true);
      setFailed(false);
      // 출석부 갱신이 실패해도(응답 인터셉터가 경고창을 띄운다) 이미 있는 출석부 목록은 보여 준다
      await request.updateAttendanceBoard().catch(() => {});
      const params = { page: pageIndex + 1 };
      if (status) params.status = status;
      if (locationState.searchText) params.className = locationState.searchText;
      request
        .getOfflineClassesBySchedule(params)
        .then(({ success, classes, pagination, statusCounts }) => {
          if (success) {
            setList(classes);
            setTotal(pagination.total);
            setStatusCounts(statusCounts || {});
          }
        })
        // 실패는 응답 인터셉터가 경고창으로 알린다. 빈 목록을 「결과 0건」 으로 보이지 않게 표시만 바꾼다
        .catch(() => setFailed(true))
        .finally(() => setLoading(false));
    },
    [status, locationState.searchText],
  );

  // AdminRoute 가 기록이 바뀔 때마다 이 페이지를 새로 마운트하고 Table 은 기록 state 의 pageIndex 로
  // 시작하므로, pageIndex 0 만 넣으면 1페이지부터 다시 조회한다(D-67)
  const onClickSearch = useCallback(() => {
    setLocationState({ searchText, pageIndex: 0 });
  }, [setLocationState, searchText]);
  const onClickReset = useCallback(() => {
    setLocationState({ searchText: '', pageIndex: 0 });
  }, [setLocationState]);

  const onToggleUnused = useCallback((attendanceBoardId, isUnused) => {
    request
      .updateAttendanceBoardUnused({ attendanceBoardId, isUnused })
      .then(({ success, data }) => {
        if (success) {
          setList((prev) => prev.map((row) => (row.id === data.id ? { ...row, isUnused: data.isUnused } : row)));
        }
      })
      .catch(() => {}); // 실패는 응답 인터셉터가 경고창으로 알리고 스위치는 그대로 둔다
  }, []);

  const columns = useMemo(
    () => [
      { Header: 'ID', accessor: 'id', width: 70 },
      { Header: '강의ID', accessor: 'classId', width: 70 },
      {
        Header: '강의명',
        accessor: 'className',
        // 미사용은 색만으로 알리지 않고 칩 글자로도 알린다. 행 글자색은 List.scss 가 이 칩으로 고른다
        Cell: ({ row, value }) => (
          <div className="attendance__class">
            {row.original.isUnused && (
              <Chip size="small" variant="outlined" label="미사용" className="attendance__unused-chip" />
            )}
            <span className="attendance__title" title={value}>
              {value}
            </span>
          </div>
        ),
      },
      {
        Header: '상태',
        accessor: 'status',
        width: 80,
        Cell: ({ value }) => {
          const item = STATUSES.find((s) => s.value === value);
          return item ? <Chip size="small" label={item.label} color={item.color} /> : null;
        },
      },
      {
        Header: () => (
          <span className="attendance__sorted" title="시작일 최신순으로 정렬됩니다">
            시작일
            <ArrowDownwardIcon fontSize="inherit" />
          </span>
        ),
        id: 'startDate',
        accessor: 'startDate',
        width: 110,
      },
      { Header: '종료일', accessor: 'endDate', width: 110 },
      { Header: '수업요일', accessor: 'classDays', width: 100, Cell: ({ value }) => toWeekdays(value) },
      {
        Header: '수업 시간',
        id: 'time',
        width: 120,
        accessor: (row) => `${toHourMinute(row.startTime)} ~ ${toHourMinute(row.endTime)}`,
      },
      { Header: '강의실', accessor: 'classroom', width: 140 },
      { Header: '수강생', accessor: 'studentCount', width: 70 },
      {
        Header: '미사용',
        accessor: 'isUnused',
        width: 80,
        Cell: ({ row, value }) => (
          <Switch
            size="small"
            color="pink"
            checked={value}
            onChange={(e) => onToggleUnused(row.original.id, e.target.checked)}
            inputProps={{ 'aria-label': `${row.original.className} 미사용` }}
          />
        ),
      },
    ],
    [onToggleUnused],
  );

  // 미사용 칸은 스위치 둘레 여백까지 눌러도 상세를 열지 않는다(스위치를 누르려다 새 탭이 뜨지 않게)
  const goToPage = useCallback((row, e) => {
    if (e?.target.closest('td')?.querySelector('.MuiSwitch-root')) return;
    const { classId, id } = row.original;
    window.open(`/lecture/${classId}/attendance/${id}`, '_blank');
  }, []);

  return (
    <ThemeProvider theme={EJE_THEME}>
      <div className="attendance">
        <div className="attendance__card">
          <div className="attendance__toolbar">
            <Tabs
              className="attendance__tabs"
              value={status}
              onChange={(e, value) => setLocationState({ status: value, pageIndex: 0 })}
            >
              {STATUSES.map(({ value, label }) => {
                const count = statusCounts[value || 'total'];
                return (
                  <Tab
                    key={label}
                    value={value}
                    label={count === undefined ? label : `${label} ${count.toLocaleString()}`}
                  />
                );
              })}
            </Tabs>
          </div>
          <div className="attendance__filters">
            <OutlinedInput
              className="attendance__search"
              size="small"
              color="pink"
              value={searchText}
              placeholder="강의명으로 검색"
              startAdornment={
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              }
              onChange={(e) => setSearchText(e.target.value)}
              onKeyDown={(e) => {
                if (isEnter(e)) onClickSearch();
              }}
            />
            <Button.MUI variant="contained" color="pink" disableElevation onClick={onClickSearch}>
              검색
            </Button.MUI>
            <Button.MUI color="inherit" onClick={onClickReset}>
              초기화
            </Button.MUI>
          </div>
          <div className="attendance__summary">
            <span>총 {total.toLocaleString()}건</span>
            <span>시작일 최신순</span>
          </div>
          <Table
            variant="clean"
            data={list}
            columns={columns}
            total={total}
            fetchData={fetchData}
            onClickRow={goToPage}
          />
          {list.length === 0 && !loading && (
            <div className="attendance__empty">
              {failed ? '목록을 불러오지 못했습니다.' : '조건에 맞는 출석부가 없습니다.'}
            </div>
          )}
        </div>
      </div>
    </ThemeProvider>
  );
};
export default OfflineClassesByScheduleList;
